import { Component, Input, ElementRef } from '@angular/core';
import * as Highcharts from 'highcharts';

import { BaseHighChartComponent } from './base-chart.component';
import { ChartConfigService } from '../services/chart-config.service';

@Component({
  selector: 'app-line-chart',
  template:
    '<div class="chart-container" style="height: 100%; width: 100%;"></div>',
})
export class LineChartComponent extends BaseHighChartComponent {
  constructor(
    elementRef: ElementRef,
    protected configService: ChartConfigService,
  ) {
    super(elementRef, configService);
  }

  @Input() showLegend: boolean = true;
  @Input() enableZoom: boolean = true;
  @Input() chartTitle: string = '';
  @Input() yAxisTitle: string = '';
  @Input() xAxisTitle: string = '';
  @Input() xAxisType: 'datetime' | 'category' = 'datetime';
  @Input() xAxisCategories: string[] = [];
  @Input() xAxisTickInterval: number | undefined;
  @Input() xAxisOrdinal: boolean = false;
  @Input() yAxisLabelFormat: string = '{value}';
  @Input() tooltipOptions: Highcharts.TooltipOptions = {};

  protected override getChartConfig(): Highcharts.Options {
    const baseConfig = this.configService.getLineChartConfig();
    let xAxis: Highcharts.XAxisOptions;

    if (this.xAxisType === 'category') {
      xAxis = {
        type: 'category',
        categories: this.xAxisCategories,
        title: { text: this.xAxisTitle },
        tickInterval: this.xAxisTickInterval,
        lineColor: (baseConfig.xAxis as Highcharts.XAxisOptions).lineColor,
        tickColor: (baseConfig.xAxis as Highcharts.XAxisOptions).tickColor,
        labels: (baseConfig.xAxis as Highcharts.XAxisOptions).labels,
      };
    } else {
      xAxis = {
        ...(baseConfig.xAxis as Highcharts.XAxisOptions),
        type: 'datetime',
        ordinal: this.xAxisOrdinal,
        title: {
          text: this.xAxisTitle,
        },
        ...(this.xAxisTickInterval && {
          tickInterval: this.xAxisTickInterval,
        }),
        labels: {
          ...(baseConfig.xAxis as Highcharts.XAxisOptions).labels,
          formatter: function () {
            return Highcharts.dateFormat('%e %b', this.value as number);
          },
        },
        gridLineColor:
          (baseConfig.xAxis as Highcharts.XAxisOptions).gridLineColor ||
          'rgba(255, 255, 255, 0.05)',
        gridLineWidth: 0,
      };
    }

    return {
      ...baseConfig,
      chart: {
        ...baseConfig.chart,
        type: 'line',
        zooming: {
          type: this.enableZoom ? 'x' : undefined,
        },
      },
      title: {
        text: this.chartTitle || undefined,
      },
      navigator: { enabled: false },
      rangeSelector: { enabled: false },
      scrollbar: { enabled: false },
      series: this.chartData,
      colors:
        this.colorPalette.length > 0 ? this.colorPalette : baseConfig.colors,
      xAxis,
      yAxis: {
        ...baseConfig.yAxis,
        title: {
          text: this.yAxisTitle,
        },
        labels: {
          ...(baseConfig.yAxis as Highcharts.YAxisOptions).labels,
          format: this.yAxisLabelFormat,
        },
      },
      plotOptions: {
        ...baseConfig.plotOptions,
        series: {
          ...(baseConfig.plotOptions?.series as Highcharts.PlotSeriesOptions),
          dataGrouping: {
            enabled: true,
          },
        },
      },
      tooltip: {
        ...baseConfig.tooltip,
        ...this.tooltipOptions,
      },
    };
  }
}
